import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { SubWebsocketMessageRequest } from '@qtek/core/websockets-core';
import { Tag } from '@qtek/shared/models';
import { pipe, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { TagManagerService } from './tag-manager.service';

export type TagManagerConnectPayload = Partial<
  Omit<SubWebsocketMessageRequest, 'ent' | 'op'>
>;

export interface TagManagerState {
  tags: Tag[];
  isLoading: boolean;
}

const initialState: TagManagerState = {
  tags: [],
  isLoading: false,
};

export const TagManagerStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, service = inject(TagManagerService)) => {
    const disconnect$ = new Subject<void>();

    return {
      connect: rxMethod<TagManagerConnectPayload>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(payload =>
            service.subscribe({ ...payload }).pipe(
              takeUntil(disconnect$),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              tap(({ op, res, id }: any) => {
                switch (op) {
                  case 'query':
                    if (res) {
                      patchState(store, { tags: res, isLoading: false });
                    } else {
                      patchState(store, { isLoading: false });
                    }
                    break;
                  case 'ins':
                    if (res) {
                      patchState(store, state => ({
                        tags: [...state.tags, res],
                      }));
                    }
                    break;
                  case 'upd':
                    if (res) {
                      patchState(store, state => ({
                        tags: state.tags.map(tag =>
                          tag._id === res._id ? res : tag
                        ),
                      }));
                    }
                    break;
                  case 'del':
                    if (id) {
                      patchState(store, state => ({
                        tags: state.tags.filter(tag => tag._id !== id),
                      }));
                    }
                    break;
                }
              })
            )
          )
        )
      ),

      createTag(
        tag: Pick<Tag, 'tagName' | 'description' | 'color' | 'icon'>
      ): void {
        service.createTag(tag).subscribe();
      },

      updateTag(
        tag: Pick<Tag, 'tagName' | 'description' | 'color' | 'icon' | '_id'>
      ): void {
        service.updateTag(tag).subscribe();
      },

      deleteTag(tag: Pick<Tag, '_id'>): void {
        service.deleteTag(tag).subscribe();
      },

      disconnect(): void {
        disconnect$.next();
      },
    };
  })
);
